import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getApiErrorMessage } from "../config/api";
import Modal from "../components/ui/Modal";
import { planService } from "../services/planService";
import { hasActiveSuccessfulPlanPayment, hasApprovedMarketplaceAccess } from "../utils/marketplaceAccess";
import { clearPendingPlanSelection, persistPendingPlanSelection } from "../utils/sellerOnboarding";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CreditCard,
  Crown,
  Loader2,
  RefreshCcw,
  Sparkles,
  Zap,
} from "lucide-react";

const formatCurrency = (amount, isFree) => {
  if (isFree || amount === 0) return "Free";
  return `N${Number(amount || 0).toLocaleString()}`;
};

const accentForPlan = (plan) => {
  if (plan.isFree) {
    return {
      border: "border-gray-200 dark:border-gray-700",
      badge: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
      button:
        "bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200",
      icon: Zap,
      iconColor: "text-gray-500",
    };
  }

  if ((plan.price || 0) >= 30000) {
    return {
      border: "border-amber-500/30",
      badge: "bg-amber-500/10 text-amber-500",
      button: "bg-amber-500 text-white hover:bg-amber-400",
      icon: Crown,
      iconColor: "text-amber-400",
    };
  }

  return {
    border: "border-violet-500/30",
    badge: "bg-violet-500/10 text-violet-500",
    button: "bg-violet-600 text-white hover:bg-violet-500",
    icon: Sparkles,
    iconColor: "text-violet-400",
  };
};

const featureList = (plan) => [
  `${plan.maxProducts ?? 0} max products`,
  `${plan.maxCategories ?? 0} max categories`,
  Array.isArray(plan.markets) && plan.markets.length
    ? `Markets: ${plan.markets.map((market) => market.name).join(", ")}`
    : "Market assignment handled during onboarding",
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasSellerAccess = hasApprovedMarketplaceAccess(user);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [durationNumber, setDurationNumber] = useState(1);
  const [checkoutError, setCheckoutError] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);

  const activePlanQuery = useQuery({
    queryKey: ["pricing-active-plan-payment", user?.id],
    queryFn: async () => planService.getActivePlanPayment(user!.id),
    enabled: Boolean(user?.id) && Boolean(user?.token),
  });

  const plansQuery = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const data = await planService.listPlans();
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(user?.token),
  });

  const plans = plansQuery.data || [];
  const activePlanPayment = activePlanQuery.data || null;
  const activePlan = activePlanPayment?.plan || null;
  const loading = Boolean(user?.token) && plansQuery.isLoading;
  const error = !user?.token
    ? "You must be signed in to load plan information."
    : plansQuery.error
      ? getApiErrorMessage(plansQuery.error, "Unable to load plans.")
      : "";
  const activePlanError = activePlanQuery.error
    ? getApiErrorMessage(activePlanQuery.error, "Unable to load current plan state.")
    : "";
  const hasSuccessfulPayment = hasActiveSuccessfulPlanPayment(activePlanPayment);
  const hasPendingPayment =
    Boolean(activePlanPayment) &&
    !hasSuccessfulPayment &&
    activePlanPayment?.isCanceled !== true &&
    activePlanPayment?.isExpired !== true;

  const currentPlanName = useMemo(() => {
    return (activePlan?.name || user?.planName || user?.plan || "").toLowerCase();
  }, [activePlan?.name, user?.plan, user?.planName]);

  const currentPlanLabel = activePlan?.name || user?.planName || user?.plan || "No active plan";
  const statusLabel = hasSuccessfulPayment
    ? "Active"
    : hasPendingPayment
      ? "Pending payment"
      : activePlanPayment?.isCanceled
        ? "Canceled"
        : activePlanPayment?.isExpired
          ? "Expired"
          : hasSellerAccess
            ? "No active payment"
            : "Not started";

  const openPlanCheckout = (plan) => {
    setCheckoutError("");
    setSelectedPlan(plan);
    setDurationNumber(1);
  };

  const closePlanCheckout = () => {
    setSelectedPlan(null);
    setDurationNumber(1);
    setCheckoutError("");
    clearPendingPlanSelection();
  };

  const handlePlanPayment = async () => {
    if (!selectedPlan?.id || !user?.id || !user?.token) {
      setCheckoutError("Plan selection or profiler session is missing.");
      return;
    }

    setCheckoutError("");
    setProcessingPayment(true);

    try {
      const normalizedDuration = Math.max(1, Number(durationNumber) || 1);
      const data = await planService.makePlanPayment({
        planId: selectedPlan.id,
        profilerId: user.id,
        durationNumber: normalizedDuration,
        callbackUrl: `${window.location.origin}/payment/callback`,
      });
      const paymentUrl = data.paymentUrl || data.authorization_url;

      persistPendingPlanSelection({
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        planData: selectedPlan,
        paymentReference: data.reference,
      });

      if (paymentUrl) {
        window.location.href = paymentUrl;
        return;
      }

      if (data.reference) {
        window.location.href = `${window.location.origin}/payment/callback?reference=${encodeURIComponent(data.reference)}&planId=${encodeURIComponent(selectedPlan.id)}`;
        return;
      }

      setCheckoutError("Payment URL not provided by the backend.");
    } catch (checkoutFailure) {
      setCheckoutError(
        getApiErrorMessage(checkoutFailure, "Could not start plan payment."),
      );
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePlanAction = () => {
    if (activePlanPayment) {
      navigate("/dashboard/manage-plan");
      return;
    }

    if (plans[0]) {
      openPlanCheckout(plans[0]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2
            size={40}
            className="mx-auto animate-spin text-violet-500 mb-4"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading plan options...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-violet-500 font-semibold">
            Seller Plans
          </p>
          <h2 className="text-3xl font-black text-gray-900 dark:text-gray-100 mt-2">
            Review marketplace pricing
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
            Plan purchase, renewal, upgrade, and downgrade now stay inside the marketplace platform. The backend still handles all plan transition logic through the same payment endpoint.
          </p>
        </div>

        <button
          type="button"
          onClick={handlePlanAction}
          className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
          {activePlanPayment ? "View plan state" : "Choose a plan"}
          <ArrowRight size={15} />
        </button>
      </div>

      {(error || activePlanError) && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200 flex items-start gap-3">
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Plan list unavailable</p>
            <p className="mt-1">{error || activePlanError}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
            Current Plan
          </p>
          <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {currentPlanLabel}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {activePlan?.price === 0
              ? "Free plan"
              : activePlan?.price
                ? `${formatCurrency(activePlan.price, false)}/month`
                : "Plan data will appear here after your first successful plan payment."}
          </p>
        </article>
        <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
            Plan Status
          </p>
          <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {statusLabel}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {hasPendingPayment
              ? "A pending payment exists. Review it before starting another plan change."
              : "Use any plan below to start a new purchase or change."}
          </p>
        </article>
        <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
            Payment Reference
          </p>
          <p className="mt-3 text-lgfont-bold text-gray-900 dark:text-gray-100 break-words">
            {activePlanPayment?.transactionReference || "-"}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {activePlanPayment ? "Latest active plan payment record." : "No active plan payment found yet."}
          </p>
        </article>
      </div>

      {hasPendingPayment && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 flex items-start justify-between gap-4 flex-col sm:flex-row sm:items-center">
          <div>
            <p className="font-semibold">Pending seller plan payment</p>
            <p className="mt-1 text-sm opacity-90">
              Review the current payment state before attempting another renewal, upgrade, or downgrade.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/dashboard/manage-plan")}
            className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-400">
            <RefreshCcw size={15} />
            Open plan state
          </button>
        </div>
      )}

      {plans.length === 0 ? (
        <div className="card py-16 text-center">
          <Sparkles
            size={36}
            className="mx-auto text-gray-300 dark:text-gray-700 mb-4"
          />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            No plans available right now
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Retry when the pricing endpoint is available again.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {plans.map((plan) => {
            const isCurrent =
              currentPlanName &&
              plan.name.toLowerCase().includes(currentPlanName);
            const accent = accentForPlan(plan);
            const Icon = accent.icon;

            return (
              <article
                key={plan.id}
                className={`card p-6 flex flex-col ${accent.border} ${isCurrent ? "ring-2 ring-violet-500/40" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="w-11 h-11 rounded-2xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center mb-4">
                      <Icon size={20} className={accent.iconColor} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {plan.name}
                    </h3>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${accent.badge}`}>
                        {formatCurrency(plan.price, plan.isFree)}
                        {!plan.isFree && "/month"}
                      </span>
                      {isCurrent && (
                        <span className="rounded-full bg-violet-500/10 px-3 py-1 text-xs font-semibold text-violet-400">
                          Current plan
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <ul className="mt-6 space-y-3 flex-1">
                  {featureList(plan).map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                      <Check
                        size={16}
                        className="mt-0.5 flex-shrink-0 text-emerald-400"
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => openPlanCheckout(plan)}
                  disabled={hasPendingPayment}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition ${accent.button}`}>
                  {isCurrent
                    ? "Renew plan"
                    : hasSellerAccess
                      ? `Switch to ${plan.name}`
                      : `Choose ${plan.name}`}
                  <ArrowRight size={15} />
                </button>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={Boolean(selectedPlan)}
        onClose={closePlanCheckout}
        title={`Seller Plan Checkout · ${selectedPlan?.name ?? ""}`}
        size="md">
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800/60">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                {(selectedPlan?.price || 0) >= 30000 ? (
                  <Crown size={20} className="text-amber-400" />
                ) : selectedPlan?.isFree ? (
                  <Zap size={20} className="text-gray-400" />
                ) : (
                  <Sparkles size={20} className="text-violet-400" />
                )}
              </div>
              <div>
                <p className="font-bold text-gray-900 dark:text-gray-100">
                  {selectedPlan?.name} Plan
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formatCurrency(selectedPlan?.price, selectedPlan?.isFree)}
                  {!selectedPlan?.isFree && "/month"}
                </p>
              </div>
            </div>
            <ul className="space-y-2">
              {selectedPlan && featureList(selectedPlan).map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <Check size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800/60">
            <label className="block text-xs font-semibold uppercase tracking-wide mb-3 text-gray-500">
              Duration (months)
            </label>
            <input
              type="number"
              min="1"
              value={durationNumber}
              onChange={(event) =>
                setDurationNumber(Math.max(1, Number(event.target.value) || 1))
              }
              className="mb-4 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:ring-2 focus:ring-violet-500/50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            />
            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
              The same payment endpoint handles new purchase, renewal, upgrade, and downgrade. No frontend proration or credit calculation is performed here.
            </p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Monthly price</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(selectedPlan?.price, selectedPlan?.isFree)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Duration
                </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {durationNumber} month{durationNumber === 1 ? "" : "s"}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between text-sm font-bold text-gray-900 dark:border-gray-700 dark:text-gray-100">
                <span>Total</span>
                <span>
                  {selectedPlan?.isFree
                    ? "Free"
                    : `N${(Number(selectedPlan?.price || 0) * durationNumber).toLocaleString()}`}
                </span>
              </div>
            </div>
          </div>

          {checkoutError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
              {checkoutError}
            </div>
          )}

          <div className="rounded-xl bg-blue-50 px-4 py-3 text-xs text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
            You will be redirected to Paystack for payment. The existing callback will verify the result before updating seller access state.
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={closePlanCheckout}
              disabled={processingPayment}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePlanPayment}
              disabled={processingPayment}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
              {processingPayment ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Redirecting...
                </>
              ) : (
                <>
                  <CreditCard size={15} />
                  Continue to payment
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
